import { useNavigate } from '@tanstack/react-router';
import {
  Calendar1,
  CalendarDays,
  CalendarRange,
  Check,
  ChevronsUpDown,
  List,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { Drawer } from 'vaul';

import { useCalendar } from '@/components/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useListServices } from '@/features/organization-services';
import { cn } from '@/lib/utils';

import type { TCalendarView } from '@/components/calendar/types';

interface MobileViewOptionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: TCalendarView;
  basePath: string;
  /**
   * The views this axis has routes for. Defaults to all of them; the rooms
   * axis has no agenda, and offering it opened a blank page.
   */
  views?: readonly TCalendarView[];
}

const VIEWS: {
  value: TCalendarView;
  label: string;
  path: string;
  icon: LucideIcon;
}[] = [
  { value: 'agenda', label: 'Agenda', path: 'agenda', icon: List },
  { value: 'day', label: 'Day', path: 'day', icon: Calendar1 },
  { value: 'week', label: 'Week', path: 'week', icon: CalendarRange },
  { value: 'month', label: 'Month', path: 'month', icon: CalendarDays },
];

/**
 * The bookings drawer, opened from the header burger. It slides in from the
 * LEFT, edge to edge, the way Google Calendar's does:
 * — the views (agenda / day / week / month), the current one highlighted,
 *   routed off `basePath`;
 * — Team: whose diary to show, as a searchable combobox (single-select,
 *   mirrors UserSelect semantics; "Everyone" is the default);
 * — Services: narrow to one service, the same way.
 *
 * Choosing a view closes the drawer (you asked to go somewhere); choosing a
 * staff member or service collapses that combobox and leaves the drawer open so
 * the other filter can be adjusted while the diary updates behind it. Date selection lives in the separate date-picker
 * sheet behind the header's date dropdown.
 */
export function MobileViewOptionsSheet({
  open,
  onOpenChange,
  view,
  basePath,
  views,
}: MobileViewOptionsSheetProps) {
  const {
    selectedUserId,
    setSelectedUserId,
    selectedServiceId,
    setSelectedServiceId,
    users,
    colorBy,
    setColorBy,
    config,
  } = useCalendar();
  const { services } = useListServices();
  const navigate = useNavigate();
  // The combobox lists render INSIDE the drawer (see FilterCombobox).
  const [drawerEl, setDrawerEl] = useState<HTMLElement | null>(null);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} direction="left">
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Drawer.Content
          ref={setDrawerEl}
          className="fixed inset-y-0 left-0 z-[100] flex w-[300px] max-w-[85vw] flex-col overflow-hidden bg-background pt-[max(20px,env(safe-area-inset-top,0px))] pb-[max(16px,env(safe-area-inset-bottom,0px))] shadow-xl outline-none"
        >
          <Drawer.Title className="px-5 pb-4 text-[22px] font-semibold tracking-[-0.02em]">
            Bookings
          </Drawer.Title>
          <Drawer.Description className="sr-only">
            Choose a calendar view and filter by team member or service.
          </Drawer.Description>

          <div className="min-h-0 flex-1 overflow-y-auto border-t">
            <nav aria-label="Calendar view" className="flex flex-col py-2">
              {VIEWS.filter(
                (option) => !views || views.includes(option.value)
              ).map((option) => (
                <DrawerRow
                  key={option.value}
                  icon={option.icon}
                  active={view === option.value}
                  onClick={() => {
                    onOpenChange(false);
                    if (view !== option.value) {
                      void navigate({
                        to: `${basePath}/${option.path}` as never,
                      });
                    }
                  }}
                >
                  {option.label}
                </DrawerRow>
              ))}
            </nav>

            <Section title="Team">
              <FilterCombobox
                container={drawerEl}
                label="Team member"
                value={selectedUserId}
                onChange={setSelectedUserId}
                allValue="all"
                allLabel="Everyone"
                searchPlaceholder="Search team…"
                emptyText="No team members found."
                options={users.map((user) => ({
                  value: user.id,
                  label: user.name,
                }))}
              />
            </Section>

            {config.colorByEnabled && (
              <Section title="Color by">
                <FilterCombobox
                  container={drawerEl}
                  label="Color by"
                  value={colorBy}
                  onChange={(value) =>
                    setColorBy(value === 'service' ? 'service' : 'team-member')
                  }
                  allValue="team-member"
                  allLabel="Team member"
                  emptyText=""
                  options={[{ value: 'service', label: 'Service' }]}
                />
              </Section>
            )}

            {services.length > 0 && (
              <Section title="Services">
                <FilterCombobox
                  container={drawerEl}
                  label="Service"
                  value={selectedServiceId}
                  onChange={setSelectedServiceId}
                  allValue="all"
                  allLabel="All services"
                  searchPlaceholder="Search services…"
                  emptyText="No services found."
                  options={services.map((service) => ({
                    value: service.id,
                    label: service.name,
                  }))}
                />
              </Section>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t py-2">
      <h3 className="px-5 pt-2 pb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {title}
      </h3>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

/** A view: full-bleed row, icon then label, the current one tinted. */
function DrawerRow({
  icon: Icon,
  active,
  onClick,
  children,
}: {
  icon: LucideIcon;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'mr-3 flex h-12 items-center gap-5 rounded-r-full px-5 text-left text-[15px] font-medium transition-colors',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-foreground active:bg-accent'
      )}
    >
      <Icon className="size-5 shrink-0" strokeWidth={2} aria-hidden />
      {children}
    </button>
  );
}

/**
 * A searchable single-select. Its list floats over the drawer, portaled INTO
 * the drawer (`container`): a popover at the end of `body` would sit outside the
 * drawer's modal layer, which blocks taps on anything outside itself, so its
 * options could be seen but not chosen.
 */
function FilterCombobox({
  container,
  label,
  value,
  onChange,
  options,
  allValue,
  allLabel,
  searchPlaceholder,
  emptyText,
}: {
  container: HTMLElement | null;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  allValue: string;
  allLabel: string;
  searchPlaceholder?: string;
  emptyText: string;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const all = { value: allValue, label: allLabel };
  const choices = [all, ...options];
  const selected = choices.find((option) => option.value === value) ?? all;

  return (
    <div className="px-4 pb-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-label={label}
            aria-expanded={open}
            aria-controls={listId}
            className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border bg-background px-3 text-left text-[15px] active:bg-accent"
          >
            <span className="min-w-0 truncate">{selected.label}</span>
            <ChevronsUpDown
              className="size-4 shrink-0 opacity-50"
              aria-hidden
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          container={container}
          align="start"
          // Above the drawer (z-100) it is portaled into, as wide as the button.
          className="z-[110] w-(--radix-popover-trigger-width) p-0"
        >
          <Command tabIndex={searchPlaceholder ? undefined : 0}>
            {searchPlaceholder && (
              <CommandInput placeholder={searchPlaceholder} />
            )}
            <CommandList id={listId} className="max-h-64">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {choices.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        option.value === value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
